import { LocalVideoTrack, Room } from 'twilio-video';
import { useEffect, useCallback } from 'react';
import { SELECTED_BACKGROUND_SETTINGS_KEY } from '../../../constants';
import {
  GaussianBlurBackgroundProcessor,
  VirtualBackgroundProcessor,
  ImageFit,
  isSupported,
} from '@twilio/video-processors';
import Abstract from '../../../assets/images/Abstract.jpg';
import AbstractThumb from '../../../assets/images/thumb/Abstract.jpg';
import BohoHome from '../../../assets/images/BohoHome.jpg';
import BohoHomeThumb from '../../../assets/images/thumb/BohoHome.jpg';
import Bookshelf from '../../../assets/images/Bookshelf.jpg';
import BookshelfThumb from '../../../assets/images/thumb/Bookshelf.jpg';
import CoffeeShop from '../../../assets/images/CoffeeShop.jpg';
import CoffeeShopThumb from '../../../assets/images/thumb/CoffeeShop.jpg';
import Contemporary from '../../../assets/images/Contemporary.jpg';
import ContemporaryThumb from '../../../assets/images/thumb/Contemporary.jpg';
import CozyHome from '../../../assets/images/CozyHome.jpg';
import CozyHomeThumb from '../../../assets/images/thumb/CozyHome.jpg';
import Desert from '../../../assets/images/Desert.jpg';
import DesertThumb from '../../../assets/images/thumb/Desert.jpg';
import Fishing from '../../../assets/images/Fishing.jpg';
import FishingThumb from '../../../assets/images/thumb/Fishing.jpg';
import Flower from '../../../assets/images/Flower.jpg';
import FlowerThumb from '../../../assets/images/thumb/Flower.jpg';
import Kitchen from '../../../assets/images/Kitchen.jpg';
import KitchenThumb from '../../../assets/images/thumb/Kitchen.jpg';
import ModernHome from '../../../assets/images/ModernHome.jpg';
import ModernHomeThumb from '../../../assets/images/thumb/ModernHome.jpg';
import Nature from '../../../assets/images/Nature.jpg';
import NatureThumb from '../../../assets/images/thumb/Nature.jpg';
import Ocean from '../../../assets/images/Ocean.jpg';
import OceanThumb from '../../../assets/images/thumb/Ocean.jpg';
import Patio from '../../../assets/images/Patio.jpg';
import PatioThumb from '../../../assets/images/thumb/Patio.jpg';
import Plant from '../../../assets/images/Plant.jpg';
import PlantThumb from '../../../assets/images/thumb/Plant.jpg';
import SanFrancisco from '../../../assets/images/SanFrancisco.jpg';
import SanFranciscoThumb from '../../../assets/images/thumb/SanFrancisco.jpg';
import { Thumbnail } from '../../BackgroundSelectionDialog/BackgroundThumbnail/BackgroundThumbnail';
import { useLocalStorageState } from '../../../hooks/useLocalStorageState/useLocalStorageState';

export interface BackgroundSettings {
  type: Thumbnail;
  index?: number;
}

const imageNames: string[] = [
  'Abstract',
  'Boho Home',
  'Bookshelf',
  'Coffee Shop',
  'Contemporary',
  'Cozy Home',
  'Desert',
  'Fishing',
  'Flower',
  'Kitchen',
  'Modern Home',
  'Nature',
  'Ocean',
  'Patio',
  'Plant',
  'San Francisco',
];

const images = [
  AbstractThumb,
  BohoHomeThumb,
  BookshelfThumb,
  CoffeeShopThumb,
  ContemporaryThumb,
  CozyHomeThumb,
  DesertThumb,
  FishingThumb,
  FlowerThumb,
  KitchenThumb,
  ModernHomeThumb,
  NatureThumb,
  OceanThumb,
  PatioThumb,
  PlantThumb,
  SanFranciscoThumb,
];

const rawImagePaths = [
  Abstract,
  BohoHome,
  Bookshelf,
  CoffeeShop,
  Contemporary,
  CozyHome,
  Desert,
  Fishing,
  Flower,
  Kitchen,
  ModernHome,
  Nature,
  Ocean,
  Patio,
  Plant,
  SanFrancisco,
];

let imageElements = new Map();

const getImage = (index: number): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    if (imageElements.has(index)) {
      return resolve(imageElements.get(index));
    }
    const img = new Image();
    img.onload = () => {
      imageElements.set(index, img);
      resolve(img);
    };
    img.onerror = reject;
    img.src = rawImagePaths[index];
  });
};

export const backgroundConfig = {
  imageNames,
  images,
};

const virtualBackgroundAssets = '/virtualbackground';
let blurProcessor: GaussianBlurBackgroundProcessor;
let virtualBackgroundProcessor: VirtualBackgroundProcessor;

export default function useBackgroundSettings(videoTrack: LocalVideoTrack | undefined, room?: Room | null) {
  const [backgroundSettings, setBackgroundSettings] = useLocalStorageState<BackgroundSettings>(
    SELECTED_BACKGROUND_SETTINGS_KEY,
    { type: 'none', index: 0 }
  );

  const removeProcessor = useCallback(() => {
    if (videoTrack && videoTrack.processor) {
      videoTrack.removeProcessor(videoTrack.processor);
    }
  }, [videoTrack]);

  const addProcessor = useCallback(
    (processor: GaussianBlurBackgroundProcessor | VirtualBackgroundProcessor) => {
      if (!videoTrack || videoTrack.processor === processor) {
        return;
      }
      removeProcessor();
      videoTrack.addProcessor(processor);
    },
    [videoTrack, removeProcessor]
  );

  useEffect(() => {
    if (!isSupported) {
      return;
    }
    // make sure localParticipant has joined room before applying video processors
    // this ensures that the video processors are not applied on the LocalVideoPreview
    const handleProcessorChange = async () => {
      if (!blurProcessor) {
        blurProcessor = new GaussianBlurBackgroundProcessor({
          assetsPath: virtualBackgroundAssets,
        });
        await blurProcessor.loadModel();
      }
      if (!virtualBackgroundProcessor) {
        virtualBackgroundProcessor = new VirtualBackgroundProcessor({
          assetsPath: virtualBackgroundAssets,
          backgroundImage: await getImage(0),
          fitType: ImageFit.Cover,
        });
        await virtualBackgroundProcessor.loadModel();
      }
      if (!room?.localParticipant) {
        return;
      }

      if (backgroundSettings.type === 'blur') {
        addProcessor(blurProcessor);
      } else if (backgroundSettings.type === 'image' && typeof backgroundSettings.index === 'number') {
        virtualBackgroundProcessor.backgroundImage = await getImage(backgroundSettings.index);
        addProcessor(virtualBackgroundProcessor);
      } else {
        removeProcessor();
      }
    };
    handleProcessorChange();
  }, [backgroundSettings, videoTrack, room, addProcessor, removeProcessor]);

  return [backgroundSettings, setBackgroundSettings] as const;
}
